import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { OryAuthFacade } from '@ory-auth';
import { Session } from '@ory/client';
import { find as _find, get, isEmpty, isNil } from 'lodash-es';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  concatMap,
  filter,
  map,
  share,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { NavSegments, uuid } from '@aw/shared/models';

import { BusinessUnit, JobTitle, Module, Page } from '@aw/prypco/enums';
import {
  AccountState,
  AccountStates,
  ExternalIdentifier,
  UserIdentifier,
  UserInfo,
  UserService,
} from '@aw/prypco/services/user';
import { SplashScreenFacade } from '@aw/prypco/state/splash-screen';

import {
  composeAccountStatesRecord,
  composeLeadIdRecord,
  composeLeadRecord,
} from './auth-state.util';
import * as AuthActions from './auth.actions';
import * as AuthSelectors from './auth.selectors';
import { LeadIdRecord } from './models/entities/lead-id-record.model';
import {
  LeadBaseRecord,
  LeadRecord,
} from './models/entities/lead-record.model';
import {
  MemberInfo,
  MemberInfoCustomer,
} from './models/entities/member-info.model';
import { AuthUtils } from './utils/auth.utils';

@Injectable({
  providedIn: 'root',
})
export class AuthFacade {
  // #region Injections
  private readonly store: Store = inject(Store);
  private readonly userService = inject(UserService);
  private readonly oryAuthFacade = inject(OryAuthFacade);
  private readonly splashScreenFacade = inject(SplashScreenFacade);
  // #endregion

  // #region Public Auth variables
  public readonly initializing$: Observable<boolean>;
  public readonly initialized$: Observable<boolean>;

  public readonly sessionLoading$: Observable<boolean>;
  public readonly session$: Observable<Session | null>;

  public readonly userLoading$: Observable<boolean>;
  public readonly user$: Observable<UserInfo | null>;
  public readonly id$: Observable<uuid>;

  public readonly leadLoading$: Observable<boolean>;
  public readonly accountStatesRefreshing$: Observable<boolean>;

  public readonly stable$: Observable<boolean>;

  public readonly authenticated$: Observable<boolean>;
  public readonly unAuthenticated$: Observable<boolean>;
  // #endregion

  // #region Public User variables
  public readonly canCall$: Observable<boolean>;
  public readonly jobTitle$: Observable<JobTitle>;
  public readonly firstName$: Observable<string>;
  public readonly lastName$: Observable<string>;
  public readonly fullName$: Observable<string>;
  public readonly customerId$: Observable<uuid>;
  public readonly initials$: Observable<string>;
  // #endregion

  // #region Private state variables
  private readonly accountStates$: Observable<AccountStates>;
  private readonly leadIds$: Observable<LeadIdRecord>;
  private readonly leads$: Observable<LeadRecord>;
  // #endregion

  // #region Private Auth variables
  private readonly initializingSubject = new BehaviorSubject<boolean>(false);
  private readonly initializedSubject = new BehaviorSubject<boolean>(false);

  private readonly sessionLoadingSubject = new BehaviorSubject<boolean>(false);
  private readonly sessionSubject = new BehaviorSubject<Session | null>(null);

  private readonly userLoadingSubject = new BehaviorSubject<boolean>(false);
  private readonly userSubject = new BehaviorSubject<UserInfo | null>(null);

  private readonly accountStatesRefreshingSubject =
    new BehaviorSubject<boolean>(false);
  // #endregion

  constructor() {
    this.initializing$ = this.initializingSubject.asObservable();
    this.initialized$ = this.initializedSubject.asObservable();

    this.sessionLoading$ = this.sessionLoadingSubject.asObservable();
    this.session$ = this.sessionSubject.asObservable();

    this.userLoading$ = this.userLoadingSubject.asObservable();
    this.user$ = this.userSubject.asObservable();
    this.id$ = this.user$.pipe(
      filter((user) => !!user),
      map((user) => user!.userId),
    );

    this.canCall$ = this.getExternalIdentifier(UserIdentifier.ZiwoId).pipe(
      map((ziwoId) => !isNil(ziwoId)),
    );

    this.accountStatesRefreshing$ =
      this.accountStatesRefreshingSubject.asObservable();

    this.stable$ = combineLatest([
      this.initializing$,
      this.sessionLoading$,
      this.userLoading$,
      this.accountStatesRefreshing$,
    ]).pipe(
      map(
        ([
          initializing,
          sessionLoading,
          userLoading,
          accountStatesRefreshing,
        ]) =>
          !initializing &&
          !sessionLoading &&
          !userLoading &&
          !accountStatesRefreshing,
      ),
      filter(Boolean), // proceed only when all are false
    );

    this.authenticated$ = combineLatest([this.session$, this.user$]).pipe(
      map(([user, session]) => !!user && !!session),
    );

    this.unAuthenticated$ = this.authenticated$.pipe(
      map((authenticated) => !authenticated),
    );

    this.jobTitle$ = this.session$.pipe(
      map((session) =>
        session
          ? get(session, ['identity', 'metadata_public', 'role'], '')
          : '',
      ),
    );

    this.firstName$ = this.store.select(AuthSelectors.getFirstName);
    this.lastName$ = this.store.select(AuthSelectors.getLastName);
    this.fullName$ = this.store.select(AuthSelectors.getFullName);
    this.customerId$ = this.store.select(AuthSelectors.getCustomerId);
    this.initials$ = combineLatest([
      this.authenticated$,
      this.firstName$,
      this.lastName$,
    ]).pipe(
      filter(([authenticated]) => authenticated),
      map(([, firstName, lastName]) =>
        AuthUtils.composeInitials(firstName, lastName),
      ),
    );

    this.accountStates$ = this.store
      .select(AuthSelectors.selectAccountStates)
      .pipe(
        withLatestFrom(this.authenticated$),
        filter(([, authenticated]) => !!authenticated),
        map(([accountStates]) => accountStates),
      ) as Observable<AccountStates>;

    this.leadIds$ = this.store.select(AuthSelectors.selectLeadIdRecord).pipe(
      withLatestFrom(this.authenticated$),
      filter(
        ([leadIdRecord, authenticated]) =>
          authenticated && !isNil(leadIdRecord),
      ),
      map(([leadIdRecord]) => leadIdRecord),
    );

    this.leads$ = this.store.select(AuthSelectors.selectLeadRecord).pipe(
      withLatestFrom(this.authenticated$),
      filter(
        ([leadRecord, authenticated]) => authenticated && !isNil(leadRecord),
      ),
      map(([leadRecord]) => leadRecord),
    );
  }

  // #region Public getters/signals
  // #endregion

  // #region Public methods
  public initialize(): Observable<boolean> {
    this.initializedSubject.next(false);
    this.initializingSubject.next(true);
    this.splashScreenFacade.setLoading('auth', true);

    return this.loadSession().pipe(
      switchMap(() => this.refreshSessionAccount()),
    );
  }

  public refreshSessionAccount(): Observable<boolean> {
    return this.session$.pipe(
      switchMap((session: Session | null) => {
        if (session) {
          return this.loadUser().pipe(
            concatMap(() => this.refreshAccountStates()),
            switchMap(() => this.initializedHandler()),
          );
        }

        return this.initializedHandler();
      }),
    );
  }

  public refreshAccountStates(): Observable<boolean> {
    this.accountStatesRefreshingSubject.next(true);

    return this.user$.pipe(
      filter((user): user is UserInfo => !!user),
      map((user) => {
        const customer: MemberInfoCustomer = {
          id: user.customerId,
          firstName: user.customerFirstName,
          lastName: user.customerLastName,
        };

        return {
          leadIdRecord: composeLeadIdRecord(user),
          leadRecord: composeLeadRecord(user),
          accountStates: composeAccountStatesRecord(user),
          externalIdentifiers: user!.externalIdentifiers,
          customer: isEmpty(customer) ? undefined : customer,
        } as MemberInfo;
      }),
      withLatestFrom(this.user$),
      map(([memberInfo]) => {
        this.store.dispatch(
          AuthActions.setMemberInfo({
            ...memberInfo,
          }),
        );

        this.store.dispatch(
          AuthActions.setAccountStates({
            accountStates: memberInfo?.accountStates,
          }),
        );

        this.accountStatesRefreshingSubject.next(false);
        return true;
      }),
      share(),
    );
  }

  setLeadId(leadId: uuid, businessUnit: BusinessUnit): void {
    this.store.dispatch(AuthActions.setLeadId({ leadId, businessUnit }));
  }

  getLeadId(businessUnit: BusinessUnit): Observable<uuid | null> {
    return this.leadIds$.pipe(
      map((leadIdRecord) => get(leadIdRecord, [businessUnit], null)),
    );
  }

  getLeadRecord(businessUnit: BusinessUnit): Observable<LeadBaseRecord | null> {
    return this.leads$.pipe(
      map((leadRecord) => get(leadRecord, [businessUnit], null)),
    );
  }

  getIsProfessionalInvestor(
    businessUnit: BusinessUnit,
  ): Observable<boolean> {
    return this.store.select(AuthSelectors.selectAccountStates).pipe(
      map((accountStates) =>
        get(accountStates, [businessUnit, 'isProfessionalInvestor'], false),
      ),
    );
  }  

  getAccountState(
    businessUnit: BusinessUnit,
    accountState: AccountState,
    coerceToFalse = true,
  ): Observable<boolean> {
    return this.accountStates$.pipe(
      map((accountStates) =>
        get(
          accountStates,
          [businessUnit, accountState],
          coerceToFalse ? false : undefined,
        ),
      ),
    );
  }

  getExternalIdentifier(
    key: UserIdentifier,
  ): Observable<ExternalIdentifier | undefined> {
    return this.store.select(AuthSelectors.selectExternalIdentifiers).pipe(
      withLatestFrom(this.authenticated$),
      filter(([_, authenticated]) => !!authenticated),
      map(([externalIdentifiers]) =>
        _find(externalIdentifiers, { identifierKey: key }),
      ),
    );
  }

  getCustomerName(): Observable<{ firstName: string; lastName: string }> {
    return combineLatest([this.firstName$, this.lastName$]).pipe(
      map(([firstName, lastName]) => ({ firstName, lastName })),
    );
  }

  signUp(redirectUri: Array<string>): void {
    this.store.dispatch(AuthActions.signUp({ redirectUri }));
  }

  signUpSalesAgent(): void {
    this.store.dispatch(
      AuthActions.signUpSalesAgent({
        redirectUri: [Module.BackOffice, Page.SignUpSuccess],
      }),
    );
  }

  signUpBlocksCustomer(): void {
    this.store.dispatch(
      AuthActions.signUp({
        redirectUri: [Module.Onboarding, Page.SignUpSuccess],
      }),
    );
  }

  logOut(businessUnit?: BusinessUnit, redirectUri?: NavSegments): void {
    this.store.dispatch(AuthActions.logOut({ businessUnit, redirectUri }));
  }

  signIn(redirectUri: NavSegments): void {
    this.store.dispatch(AuthActions.signIn({ redirectUri }));
  }
  // #endregion

  // #region Private methods
  private initializedHandler(): Observable<boolean> {
    this.initializedSubject.next(true);
    this.initializingSubject.next(false);
    this.splashScreenFacade.setLoading('auth', false);

    return this.initialized$;
  }

  private loadSession(): Observable<Session | null> {
    this.oryAuthFacade.loadSession();
    this.sessionSubject.next(null);

    return this.oryAuthFacade.sessionLoading$.pipe(
      filter((loading) => !loading),
      concatMap(() => this.oryAuthFacade.session$),
      tap((session) => {
        this.sessionSubject.next(session);
        this.sessionLoadingSubject.next(false);
      }),
      switchMap(() => this.session$),
      share(),
    );
  }

  private loadUser(): Observable<UserInfo | null> {
    this.userLoadingSubject.next(true);
    this.userSubject.next(null);

    return this.userService.getUserInfo().pipe(
      tap((user) => {
        this.userSubject.next(user);
        this.userLoadingSubject.next(false);
      }),
      switchMap(() => this.user$),
      share(),
    );
  }

  private setAccountState(
    businessUnit: BusinessUnit,
    accountState: AccountState,
    value: boolean,
  ) {
    this.store.dispatch(
      AuthActions.setAccountState({ businessUnit, accountState, value }),
    );
  }

  private setInitialized(): void {
    this.store.dispatch(AuthActions.setInitialized());
  }

  private reset() {
    this.initializedSubject.next(false);
    this.sessionSubject.next(null);
    this.userSubject.next(null);
  }

  setRemoteConfig(config: Record<string, any>): void {
    this.store.dispatch(AuthActions.setRemoteConfig({ config }));
  }  

  getCountryOfResidence(
    businessUnit: BusinessUnit,
  ): Observable<string | null> {
    return this.user$.pipe(
      map((user) =>
        get(user, ['businessUnits', businessUnit, 'countryOfResidence'], null)
      )
    );
  }  

  public readonly remoteConfig$ = this.store.select(
    AuthSelectors.selectRemoteConfig
  );  
  // #endregion
}
